import { useState, useEffect, useMemo, useCallback } from "react";
import {
  Plus,
  Search,
  Pencil,
  Trash2,
  Tags,
  Lock,
  Archive,
  ArchiveRestore,
  Merge,
  RefreshCw,
} from "lucide-react";
import DashboardLayout from "../components/DashboardLayout";
import Modal from "../components/Modal";
import DeleteConfirmationModal from "../components/DeleteConfirmationModal";
import SearchableDropdown from "../components/SearchableDropdown";
import { withAuth } from "@/app/components/withAuth";
import { mastersApi, apiErrorMessage, type ApiError } from "@/lib/api";
import { useToast } from "@/context/ToastContext";
import { useAuth } from "@/hooks/useAuth";
import { formatCurrencyFull } from "@/lib/data";
import { byNewest } from "@/lib/utils";

interface ExpenseCategory {
  id: number;
  name: string;
  slug?: string;
  description: string | null;
  icon: string | null;
  is_system: boolean;
  is_active: boolean;
  expenses_count: number;
  /** ₹ spent in this calendar month (decimal string), null when nothing was spent */
  month_total: string | number | null;
  created_at?: string;
}

type SortKey = "newest" | "name" | "used";

/** Roles the backend lets create, edit, archive, merge and delete categories. */
const MANAGER_ROLES = ["super_admin", "admin", "master", "manager"];
const SYSTEM_HINT = "Filled automatically from vendor payments";

const expensesLabel = (n: number) => `${n} expense${n === 1 ? "" : "s"}`;
const isActive = (c: ExpenseCategory) => c.is_active !== false;
const byName = (a: ExpenseCategory, b: ExpenseCategory) =>
  a.name.localeCompare(b.name, undefined, { sensitivity: "base" });

const inputClass =
  "w-full h-10 px-3 rounded-xl border border-stone-200 bg-white text-sm focus:border-amber-400 focus:ring-2 focus:ring-amber-100 outline-none";
const primaryButton =
  "min-h-10 px-4 py-2 rounded-xl bg-amber-600 text-white font-semibold text-sm hover:bg-amber-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center justify-center gap-2";
const secondaryButton =
  "min-h-10 px-4 py-2 rounded-xl bg-stone-100 text-stone-600 font-semibold text-sm hover:bg-stone-200 transition-colors border border-stone-200 disabled:opacity-50 inline-flex items-center justify-center gap-2";

function Spinner({ className = "h-4 w-4 border-white" }: { className?: string }) {
  return <span className={`inline-block animate-spin rounded-full border-2 border-t-transparent ${className}`} />;
}

function CategoryBadges({ category }: { category: ExpenseCategory }) {
  return (
    <>
      {category.is_system && (
        <span
          className="inline-flex items-center gap-1 text-[11px] font-medium bg-stone-100 text-stone-600 px-1.5 py-0.5 rounded-md"
          title={SYSTEM_HINT}
        >
          <Lock className="w-3 h-3" />
          System
        </span>
      )}
      {!isActive(category) && (
        <span className="inline-flex items-center gap-1 text-[11px] font-medium bg-stone-100 text-stone-500 px-1.5 py-0.5 rounded-md border border-dashed border-stone-300">
          <Archive className="w-3 h-3" />
          Archived
        </span>
      )}
    </>
  );
}

function CategoryDetails({ category }: { category: ExpenseCategory }) {
  return (
    <>
      {category.description && (
        <p className="text-xs text-stone-500 mt-0.5 line-clamp-2">{category.description}</p>
      )}
      {category.is_system && (
        <p className="text-xs text-stone-400 mt-0.5 flex items-center gap-1">
          <Lock className="w-3 h-3 shrink-0" />
          {SYSTEM_HINT}
        </p>
      )}
    </>
  );
}

// Small labelled action used in the desktop table
function TableAction({
  onClick,
  icon: Icon,
  label,
  variant = "default",
  disabled = false,
}: {
  onClick: () => void;
  icon: React.ElementType;
  label: string;
  variant?: "default" | "danger";
  disabled?: boolean;
}) {
  const colors = variant === "danger" ? "text-red-600 hover:bg-red-50" : "text-stone-600 hover:bg-stone-100";
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={`flex items-center gap-1.5 px-2 py-1.5 rounded-lg text-xs font-medium transition-colors disabled:opacity-50 ${colors}`}
    >
      <Icon className="w-4 h-4" />
      {label}
    </button>
  );
}

// Touch-sized action used in the mobile cards
function CardAction({
  onClick,
  icon: Icon,
  label,
  variant = "default",
  disabled = false,
}: {
  onClick: () => void;
  icon: React.ElementType;
  label: string;
  variant?: "default" | "danger";
  disabled?: boolean;
}) {
  const colors =
    variant === "danger" ? "bg-red-50 text-red-600 hover:bg-red-100" : "bg-stone-100 text-stone-600 hover:bg-stone-200";
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={`h-10 min-w-10 px-3 rounded-lg flex items-center justify-center gap-1.5 text-sm font-medium disabled:opacity-50 ${colors}`}
    >
      <Icon className="w-4 h-4" />
      {label}
    </button>
  );
}

function ExpenseCategoriesPage() {
  const { showToast } = useToast();
  const { user } = useAuth();
  const canManage = MANAGER_ROLES.includes((user?.role || "").toLowerCase());

  const [categories, setCategories] = useState<ExpenseCategory[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  const [searchQuery, setSearchQuery] = useState("");
  const [showArchived, setShowArchived] = useState(false);
  const [sortBy, setSortBy] = useState<SortKey>("newest");

  // Add / edit
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<ExpenseCategory | null>(null);
  const [formName, setFormName] = useState("");
  const [formDescription, setFormDescription] = useState("");
  const [nameError, setNameError] = useState<string | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  // Merge
  const [merging, setMerging] = useState<ExpenseCategory | null>(null);
  const [mergeTargetId, setMergeTargetId] = useState("");
  const [mergeSaving, setMergeSaving] = useState(false);

  // Delete
  const [deleting, setDeleting] = useState<ExpenseCategory | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  // Archive / restore in flight
  const [busyId, setBusyId] = useState<number | null>(null);

  const loadData = useCallback(async () => {
    try {
      const response = await mastersApi.expenseCategories.list();
      const rows = Array.isArray(response?.data)
        ? response.data
        : Array.isArray(response?.data?.data)
          ? response.data.data
          : [];
      setCategories(rows);
      setLoadError(null);
    } catch (error) {
      const message = apiErrorMessage(error, "Couldn't load expense categories");
      setLoadError(message);
      showToast(message, "error");
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const archivedCount = useMemo(() => categories.filter((c) => !isActive(c)).length, [categories]);

  const visibleCategories = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    const rows = categories.filter(
      (c) =>
        (showArchived || isActive(c)) &&
        (!q || c.name.toLowerCase().includes(q) || (c.description || "").toLowerCase().includes(q))
    );
    if (sortBy === "name") return [...rows].sort(byName);
    if (sortBy === "used")
      return [...rows].sort(
        (a, b) =>
          (b.expenses_count || 0) - (a.expenses_count || 0) ||
          Number(b.month_total || 0) - Number(a.month_total || 0) ||
          byNewest(a, b)
      );
    return [...rows].sort(byNewest);
  }, [categories, searchQuery, showArchived, sortBy]);

  // Categories a merge can move expenses into: every other non-system one, active first
  const mergeOptions = useMemo(() => {
    if (!merging) return [];
    return categories
      .filter((c) => c.id !== merging.id && !c.is_system)
      .sort((a, b) => Number(isActive(b)) - Number(isActive(a)) || byName(a, b))
      .map((c) => ({ value: String(c.id), label: isActive(c) ? c.name : `${c.name} (archived)` }));
  }, [categories, merging]);
  const mergeTarget = categories.find((c) => String(c.id) === mergeTargetId) || null;

  // ---- Add / edit -------------------------------------------------------

  const openForm = (category: ExpenseCategory | null) => {
    setEditing(category);
    setFormName(category?.name || "");
    setFormDescription(category?.description || "");
    setNameError(null);
    setFormError(null);
    setFormOpen(true);
  };

  const closeForm = () => {
    if (saving) return;
    setFormOpen(false);
    setEditing(null);
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    const name = formName.trim();
    if (!name) {
      setNameError("Enter a name for the category.");
      return;
    }
    const payload = { name, description: formDescription.trim() || null };

    setSaving(true);
    setNameError(null);
    setFormError(null);
    try {
      const response = editing
        ? await mastersApi.expenseCategories.update(editing.id, payload)
        : await mastersApi.expenseCategories.create(payload);
      showToast(response?.message || (editing ? "Category updated" : "Category added"), "success");
      setFormOpen(false);
      setEditing(null);
      loadData();
    } catch (error) {
      const fieldError = (error as ApiError)?.errors?.name?.[0];
      if (fieldError) {
        // Keep the modal open so the name can be changed
        setNameError(fieldError);
      } else {
        const message = apiErrorMessage(error, "Couldn't save the category");
        setFormError(message);
        showToast(message, "error");
      }
    } finally {
      setSaving(false);
    }
  };

  // ---- Archive / restore ------------------------------------------------

  const toggleArchived = async (category: ExpenseCategory) => {
    setBusyId(category.id);
    try {
      const response = await mastersApi.expenseCategories.update(category.id, { is_active: !isActive(category) });
      showToast(response?.message || (isActive(category) ? "Category archived" : "Category restored"), "success");
    } catch (error) {
      showToast(apiErrorMessage(error, "Couldn't update the category"), "error");
    } finally {
      setBusyId(null);
      loadData();
    }
  };

  // ---- Merge ------------------------------------------------------------

  const openMerge = (category: ExpenseCategory) => {
    setMerging(category);
    setMergeTargetId("");
  };

  const closeMerge = () => {
    if (mergeSaving) return;
    setMerging(null);
    setMergeTargetId("");
  };

  const handleMerge = async () => {
    if (!merging || !mergeTarget) return;
    setMergeSaving(true);
    try {
      const response = await mastersApi.expenseCategories.merge(merging.id, mergeTarget.id);
      showToast(response?.message || `Moved expenses to “${mergeTarget.name}”`, "success");
      setMerging(null);
      setMergeTargetId("");
    } catch (error) {
      showToast(apiErrorMessage(error, "Couldn't merge the categories"), "error");
    } finally {
      setMergeSaving(false);
      loadData();
    }
  };

  // ---- Delete -----------------------------------------------------------

  const handleDelete = async () => {
    if (!deleting) return;
    setDeleteLoading(true);
    try {
      const response = await mastersApi.expenseCategories.delete(deleting.id);
      showToast(response?.message || "Category deleted", "success");
    } catch (error) {
      showToast(apiErrorMessage(error, "Couldn't delete the category"), "error");
    } finally {
      setDeleteLoading(false);
      setDeleting(null);
      loadData();
    }
  };

  // Which actions a category offers. The system category can only be renamed;
  // a category with expenses is merged away rather than deleted.
  const actionsFor = (c: ExpenseCategory) => ({
    archive: !c.is_system,
    merge: !c.is_system && (c.expenses_count || 0) > 0,
    delete: !c.is_system && (c.expenses_count || 0) === 0,
  });

  const hasFilters = searchQuery.trim() !== "";

  return (
    <DashboardLayout>
      <p className="text-sm text-stone-500 mb-4">
        Group day-to-day spending for reports. Merge duplicates instead of deleting them.
      </p>

      {/* Toolbar: stacks on mobile, one row from md up */}
      <div className="flex flex-col gap-3 mb-4 md:flex-row md:items-center">
        <div className="relative md:flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
          <input
            type="search"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search name or description..."
            aria-label="Search categories"
            className={`${inputClass} pl-9`}
          />
        </div>
        <div className="grid grid-cols-2 gap-3 md:flex md:items-center">
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value as SortKey)}
            aria-label="Sort categories"
            className={`${inputClass} md:w-40`}
          >
            <option value="newest">Newest</option>
            <option value="name">Name A–Z</option>
            <option value="used">Most used</option>
          </select>
          <label className="h-10 px-3 rounded-xl border border-stone-200 bg-white text-sm text-stone-600 flex items-center gap-2 cursor-pointer select-none hover:border-stone-300 whitespace-nowrap">
            <input
              type="checkbox"
              checked={showArchived}
              onChange={(e) => setShowArchived(e.target.checked)}
              className="w-4 h-4 accent-amber-600"
            />
            Show archived
            {archivedCount > 0 && (
              <span className="text-xs font-medium bg-stone-100 text-stone-500 px-1.5 py-0.5 rounded-md">
                {archivedCount}
              </span>
            )}
          </label>
        </div>
        {canManage && (
          <button type="button" onClick={() => openForm(null)} className={primaryButton}>
            <Plus className="w-4 h-4" />
            Add category
          </button>
        )}
      </div>

      {loading ? (
        <div className="bg-white rounded-xl border border-stone-200 divide-y divide-stone-100" aria-busy="true">
          {[0, 1, 2, 3].map((i) => (
            <div key={i} className="p-4 flex items-center justify-between gap-4 animate-pulse">
              <div className="space-y-2 flex-1">
                <div className="h-4 w-40 bg-stone-100 rounded" />
                <div className="h-3 w-64 max-w-full bg-stone-100 rounded" />
              </div>
              <div className="h-4 w-16 bg-stone-100 rounded" />
            </div>
          ))}
        </div>
      ) : loadError && categories.length === 0 ? (
        <div className="text-center py-12 bg-stone-50 rounded-xl border border-stone-200 px-4">
          <p className="text-stone-600 font-medium">{loadError}</p>
          <button type="button" onClick={() => { setLoading(true); loadData(); }} className={`${secondaryButton} mt-4`}>
            <RefreshCw className="w-4 h-4" />
            Try again
          </button>
        </div>
      ) : visibleCategories.length === 0 ? (
        <div className="text-center py-12 bg-stone-50 rounded-xl border border-stone-200 px-4">
          <Tags className="w-12 h-12 text-stone-300 mx-auto mb-3" />
          {categories.length === 0 ? (
            <>
              <p className="text-stone-700 font-medium">No expense categories yet</p>
              <p className="text-sm text-stone-500 mt-1">Add categories like “Electricity” or “Decoration” to group your expenses.</p>
              {canManage && (
                <button type="button" onClick={() => openForm(null)} className={`${primaryButton} mt-4`}>
                  <Plus className="w-4 h-4" />
                  Add category
                </button>
              )}
            </>
          ) : (
            <>
              <p className="text-stone-700 font-medium">
                {hasFilters ? `No categories match “${searchQuery.trim()}”` : "All categories are archived"}
              </p>
              <div className="mt-4 flex flex-wrap justify-center gap-2">
                {hasFilters && (
                  <button type="button" onClick={() => setSearchQuery("")} className={secondaryButton}>
                    Clear search
                  </button>
                )}
                {!showArchived && archivedCount > 0 && (
                  <button type="button" onClick={() => setShowArchived(true)} className={secondaryButton}>
                    Show {archivedCount} archived
                  </button>
                )}
              </div>
            </>
          )}
        </div>
      ) : (
        <>
          <p className="text-xs text-stone-400 mb-2">
            {visibleCategories.length} {visibleCategories.length === 1 ? "category" : "categories"}
            {archivedCount > 0 && ` · ${archivedCount} archived${showArchived ? "" : " (hidden)"}`}
          </p>

          <div className="bg-white rounded-xl border border-stone-200 overflow-hidden">
            {/* Mobile cards */}
            <div className="md:hidden divide-y divide-stone-100">
              {visibleCategories.map((category) => {
                const actions = actionsFor(category);
                const busy = busyId === category.id;
                return (
                  <div key={category.id} className={`p-4 ${isActive(category) ? "" : "bg-stone-50/60"}`}>
                    <div className="min-w-0">
                      <div className="flex flex-wrap items-center gap-1.5">
                        <p className={`font-semibold break-words ${isActive(category) ? "text-stone-800" : "text-stone-500"}`}>
                          {category.name}
                        </p>
                        <CategoryBadges category={category} />
                      </div>
                      <CategoryDetails category={category} />
                    </div>

                    <dl className="mt-3 grid grid-cols-2 gap-3">
                      <div className="rounded-lg bg-stone-50 px-3 py-2">
                        <dt className="text-[11px] text-stone-400">Expenses</dt>
                        <dd className="text-sm font-semibold text-stone-700">{category.expenses_count || 0}</dd>
                      </div>
                      <div className="rounded-lg bg-stone-50 px-3 py-2">
                        <dt className="text-[11px] text-stone-400">This month</dt>
                        <dd className="text-sm font-semibold text-stone-700">{formatCurrencyFull(category.month_total ?? 0)}</dd>
                      </div>
                    </dl>

                    {canManage && (
                      <div className="mt-3 flex flex-wrap gap-2">
                        <CardAction onClick={() => openForm(category)} icon={Pencil} label="Edit" />
                        {actions.archive && (
                          <CardAction
                            onClick={() => toggleArchived(category)}
                            icon={isActive(category) ? Archive : ArchiveRestore}
                            label={busy ? "Saving..." : isActive(category) ? "Archive" : "Restore"}
                            disabled={busy}
                          />
                        )}
                        {actions.merge && <CardAction onClick={() => openMerge(category)} icon={Merge} label="Merge" />}
                        {actions.delete && (
                          <CardAction onClick={() => setDeleting(category)} icon={Trash2} label="Delete" variant="danger" />
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            {/* Desktop table */}
            <div className="hidden md:block overflow-x-auto">
              <table className="w-full">
                <thead className="bg-stone-50">
                  <tr>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-stone-500">Category</th>
                    <th className="text-right px-4 py-3 text-xs font-semibold text-stone-500 w-28">Expenses</th>
                    <th className="text-right px-4 py-3 text-xs font-semibold text-stone-500 w-36">This month</th>
                    {canManage && <th className="text-right px-4 py-3 text-xs font-semibold text-stone-500">Actions</th>}
                  </tr>
                </thead>
                <tbody className="divide-y divide-stone-100">
                  {visibleCategories.map((category) => {
                    const actions = actionsFor(category);
                    const busy = busyId === category.id;
                    return (
                      <tr key={category.id} className={`hover:bg-stone-50 ${isActive(category) ? "" : "bg-stone-50/60"}`}>
                        <td className="px-4 py-3 align-top">
                          <div className="flex flex-wrap items-center gap-1.5">
                            <span className={`font-medium ${isActive(category) ? "text-stone-800" : "text-stone-500"}`}>
                              {category.name}
                            </span>
                            <CategoryBadges category={category} />
                          </div>
                          <CategoryDetails category={category} />
                        </td>
                        <td className="px-4 py-3 text-right text-sm text-stone-700 align-top">
                          {category.expenses_count || 0}
                        </td>
                        <td className="px-4 py-3 text-right text-sm font-medium text-stone-700 align-top whitespace-nowrap">
                          {formatCurrencyFull(category.month_total ?? 0)}
                        </td>
                        {canManage && (
                          <td className="px-4 py-2 align-top">
                            <div className="flex justify-end gap-1 flex-wrap">
                              <TableAction onClick={() => openForm(category)} icon={Pencil} label="Edit" />
                              {actions.archive && (
                                <TableAction
                                  onClick={() => toggleArchived(category)}
                                  icon={isActive(category) ? Archive : ArchiveRestore}
                                  label={busy ? "Saving..." : isActive(category) ? "Archive" : "Restore"}
                                  disabled={busy}
                                />
                              )}
                              {actions.merge && (
                                <TableAction onClick={() => openMerge(category)} icon={Merge} label="Merge" />
                              )}
                              {actions.delete && (
                                <TableAction onClick={() => setDeleting(category)} icon={Trash2} label="Delete" variant="danger" />
                              )}
                            </div>
                          </td>
                        )}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      {/* Add / edit */}
      <Modal
        isOpen={formOpen}
        onClose={closeForm}
        title={editing ? "Edit category" : "Add category"}
        size="md"
        footer={
          <div className="flex gap-3 sm:justify-end">
            <button type="button" onClick={closeForm} disabled={saving} className={`${secondaryButton} flex-1 sm:flex-none`}>
              Cancel
            </button>
            <button type="submit" form="expense-category-form" disabled={saving} className={`${primaryButton} flex-1 sm:flex-none`}>
              {saving && <Spinner />}
              {editing ? "Save changes" : "Add category"}
            </button>
          </div>
        }
      >
        <form id="expense-category-form" onSubmit={handleSave} className="space-y-4" noValidate>
          {editing?.is_system && (
            <p className="text-xs text-stone-500 bg-stone-50 border border-stone-200 rounded-xl px-3 py-2 flex items-center gap-1.5">
              <Lock className="w-3.5 h-3.5 shrink-0" />
              System category — {SYSTEM_HINT.toLowerCase()}. You can rename it.
            </p>
          )}
          <div>
            <label htmlFor="category-name" className="block text-xs font-medium text-stone-500 mb-1.5">
              Name *
            </label>
            <input
              id="category-name"
              type="text"
              value={formName}
              onChange={(e) => {
                setFormName(e.target.value);
                if (nameError) setNameError(null);
              }}
              placeholder="e.g. Electricity, Decoration"
              maxLength={255}
              autoFocus
              aria-invalid={!!nameError}
              aria-describedby={nameError ? "category-name-error" : undefined}
              className={`${inputClass} ${nameError ? "border-rose-300 bg-rose-50 focus:border-rose-400 focus:ring-rose-100" : ""}`}
            />
            {nameError && (
              <p id="category-name-error" className="text-xs text-rose-600 mt-1">
                {nameError}
              </p>
            )}
          </div>

          <div>
            <label htmlFor="category-description" className="block text-xs font-medium text-stone-500 mb-1.5">
              Description
            </label>
            <textarea
              id="category-description"
              value={formDescription}
              onChange={(e) => setFormDescription(e.target.value)}
              placeholder="What goes in this category (optional)"
              rows={3}
              className="w-full px-3 py-2.5 rounded-xl border border-stone-200 text-sm focus:border-amber-400 focus:ring-2 focus:ring-amber-100 outline-none resize-none"
            />
          </div>

          {formError && (
            <p className="text-sm text-rose-600 bg-rose-50 border border-rose-200 rounded-xl px-3 py-2">{formError}</p>
          )}
        </form>
      </Modal>

      {/* Merge */}
      <Modal
        isOpen={!!merging}
        onClose={closeMerge}
        title="Merge category"
        size="md"
        footer={
          merging && (
            <div className="flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
              <button type="button" onClick={closeMerge} disabled={mergeSaving} className={secondaryButton}>
                Cancel
              </button>
              <button
                type="button"
                onClick={handleMerge}
                disabled={!mergeTarget || mergeSaving}
                className={primaryButton}
              >
                {mergeSaving && <Spinner />}
                Move {expensesLabel(merging.expenses_count || 0)} and delete “{merging.name}”
              </button>
            </div>
          )
        }
      >
        {merging && (
          <div className="space-y-4 min-h-72">
            <p className="text-sm text-stone-700">
              Move all {expensesLabel(merging.expenses_count || 0)} from{" "}
              <span className="font-semibold">“{merging.name}”</span> into…
            </p>
            {mergeOptions.length > 0 ? (
              <SearchableDropdown
                options={mergeOptions}
                value={mergeTargetId}
                onChange={setMergeTargetId}
                placeholder="Choose a category"
              />
            ) : (
              <p className="text-sm text-stone-500 bg-stone-50 border border-stone-200 rounded-xl px-3 py-2">
                There's no other category to merge into. Add one first.
              </p>
            )}
            {mergeTarget && !isActive(mergeTarget) && (
              <p className="text-xs text-stone-500">“{mergeTarget.name}” is archived — merging will restore it.</p>
            )}
            <p className="text-xs text-stone-500">
              “{merging.name}” is deleted afterwards. The expenses themselves are kept.
            </p>
          </div>
        )}
      </Modal>

      <DeleteConfirmationModal
        isOpen={!!deleting}
        onClose={() => !deleteLoading && setDeleting(null)}
        onConfirm={handleDelete}
        title="Delete category"
        message="This category has no expenses. Delete it?"
        itemName={deleting?.name}
        loading={deleteLoading}
      />
    </DashboardLayout>
  );
}

// "master" can manage masters on the backend too, so it may open this page.
export default withAuth(ExpenseCategoriesPage, { allowedRoles: ["admin", "super_admin", "master", "manager"] });
